import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { WidgetRecommendationActionCreateAttributes } from "~/api/Api";
import { formatNumber, isRtl } from "~/helpers/helpers";

import { apiClient } from "../../../api/ApiClient";
import { Icon } from "../../../assets/icons/Icon";
import NoData from "../../containers/NoData";
import MiddleTruncateTextPx from "../../dashboard/PageTable/MiddleTruncateUrl";
import FormLabel from "../../login/formLabel/FormLabel";
import Button from "../../mantine/button/Button";
import Switch from "../../switch/Switch";
import Modal from "../../ui/modal/Modal";
import { PaginationConfig } from "../../ui/pagination/Pagination";
import { SearchBar } from "../../ui/search/SearchBar";
import Spinner from "../../ui/spinner/Spinner";
import TextArea from "../../ui/textArea/TextArea";
import PageListItem from "../components/PagesListItem";
import { ApproveAllConfirmationModal } from "../modals/ApproveAllConfirmationModal/ApproveAllConfirmationModal";
import styles from "./Interlinks.module.scss";
import InterlinksItem from "./InterlinksItem";

interface PageDetail {
  id?: number;
  inner_text?: string;
  suggestion?: string;
  final?: string;
  active?: boolean;
  approved?: boolean;
  path?: string;
  appearances?: number;
  lang?: string;
}

export interface PageItem {
  id: number;
  path_and_query?: string;
  title?: string;
  approved?: number;
  total?: number;
  details?: PageDetail[];
}

interface EditedSuggestion {
  final: string;
  active: boolean;
  lang?: string;
}

interface ContentSchemaModalProps {
  editedSuggestion: EditedSuggestion;
  setEditedSuggestion: React.Dispatch<React.SetStateAction<EditedSuggestion>>;
}

interface InterlinksProps {
  isInitialLoading: boolean;
  data: PageItem[];
  isFetching: boolean;
  allPagesLoaded: boolean;
  pagination: PaginationConfig;
  setPagination: React.Dispatch<React.SetStateAction<PaginationConfig>>;
  currentPage?: PageItem;
  setCurrentPage: (page: PageItem) => void;
  isWidgetActionLoading?: boolean;
  onMutationSuccess: () => void;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  noData: boolean;
  approvedCount: number;
  totalCount: number;
  interlinksId: number | undefined;
  description?: string | null;
}

interface PendingRecommendationAction {
  id: number;
  status: string;
  page_id?: number;
  category_id?: number;
  variant: string;
  kind?: string;
}

const ContentSchemaModal = ({
  editedSuggestion,
  setEditedSuggestion,
}: ContentSchemaModalProps) => {
  return (
    <div className={styles.editSuggestionContent}>
      <form>
        <FormLabel label="Suggestion" />
        <div
          className={clsx({ ["rtl"]: isRtl(editedSuggestion.lang) })}
          lang={editedSuggestion.lang || undefined}
        >
          <TextArea
            minRows={6}
            value={editedSuggestion.final}
            onChange={(e) =>
              setEditedSuggestion({
                ...editedSuggestion,
                final: e.target.value,
              })
            }
            placeholder="Edit Suggestion"
            style={{ width: "100%" }}
          />
        </div>
        <div className={styles.switchContainer}>
          <FormLabel label="Approve Changes" />
          <Switch
            checked={editedSuggestion.active}
            onChange={() => {
              const newActiveValue = !editedSuggestion.active;
              setEditedSuggestion((prev) => ({
                ...prev,
                active: newActiveValue,
              }));
            }}
          />
        </div>
      </form>
    </div>
  );
};

const Interlinks: React.FC<InterlinksProps> = ({
  isInitialLoading,
  data,
  isFetching,
  allPagesLoaded,
  pagination,
  setPagination,
  currentPage,
  setCurrentPage,
  isWidgetActionLoading = false,
  onMutationSuccess,
  searchTerm,
  setSearchTerm,
  noData,
  approvedCount,
  totalCount,
  interlinksId,
  description,
}) => {
  const [isApproveAllModalOpen, setIsApproveAllModalOpen] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [lastClickedId, setLastClickedId] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editedSuggestion, setEditedSuggestion] = useState<EditedSuggestion>({
    final: "",
    active: false,
  });
  const [currentEditDetail, setCurrentEditDetail] = useState<PageDetail | null>(
    null,
  );
  const [isApproveAllClicked, setIsApproveAllClicked] = useState(false);
  const [isLoadMoreClicked, setIsLoadMoreClicked] = useState(false);

  useEffect(() => {
    if (!isWidgetActionLoading) {
      setLastClickedId(null);
      setIsEditModalOpen(false);
    }
  }, [isWidgetActionLoading]);

  useEffect(() => {
    if (!isFetching) {
      setIsApproveAllClicked(false);
      setIsLoadMoreClicked(false);
    }
  }, [isFetching]);

  useQuery({
    queryKey: ["pending-statuses-and-permissions", id],
    queryFn: async () => {
      try {
        const response =
          await apiClient.sites.widgetRecommendationActionsDetail(
            Number(id),
            {
              "page[size]": 100,
              "page[number]": pagination.currentPage,
              q: {
                category_id_eq: interlinksId,
                status_eq: "pending",
                variant_eq: "automation",
              },
            },
            {
              headers: {
                "Authorize-Member-Actions": "create",
                "Authorize-Collection-Actions": "create",
              },
            },
          );

        const pendingItems =
          response?.data?.["widget/recommendation_actions"] || [];
        const approvePermissions = // @ts-expect-error BE types
          response?.data?.authorizations?.collection.find(
            (item: string) => item === "create",
          );

        return {
          pendingItems,
          approvePermission: !!approvePermissions,
        };
      } catch (error) {
        const status =
          //@ts-expect-error BE types
          error?.response?.status ||
          (error && typeof error === "object" && "status" in error
            ? error.status
            : null);

        if (status === 403) {
          return { pendingItems: [], approvePermission: false, status: 403 };
        }

        throw error;
      }
    },
    retry: (failureCount, error) => {
      if (
        //@ts-expect-error BE types
        error?.status === 403 ||
        //@ts-expect-error BE types
        (error && typeof error === "object" && error.response?.status === 403)
      ) {
        return true;
      }

      return failureCount < 3;
    },
    retryDelay: (attemptIndex, error) => {
      if (
        //@ts-expect-error BE types
        error?.status === 403 ||
        //@ts-expect-error BE types
        (error && typeof error === "object" && error.response?.status === 403)
      ) {
        return 1000;
      }

      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
    refetchInterval: (query) => {
      if (
        query?.state?.data?.pendingItems &&
        query.state.data.pendingItems.length > 0
      ) {
        return 1000;
      }

      if (query?.state?.data?.status === 403) {
        return 1000;
      }

      return false;
    },
  });

  const {
    mutate: approve,
    mutateAsync: approveAsync,
    isPending: isApprovePending,
  } = useMutation<void, Error, WidgetRecommendationActionCreateAttributes>({
    mutationFn: async (data) => {
      await apiClient.sites.widgetRecommendationActionsCreate(Number(id), data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["pending-statuses-and-permissions", id],
      });
      const pendingStatusesData = queryClient.getQueryData<{
        pendingItems: PendingRecommendationAction[];
      }>(["pending-statuses-and-permissions", id]);

      if ((pendingStatusesData?.pendingItems ?? []).length > 0) {
        queryClient.invalidateQueries({
          queryKey: ["pending-statuses-and-permissions"],
        });

        return;
      }

      if (onMutationSuccess) {
        onMutationSuccess();
      }

      queryClient.invalidateQueries({
        queryKey: ["interlinks-pages"],
      });

      queryClient.invalidateQueries({
        queryKey: ["interlinksCategotyStats"],
      });
    },
    onError: (error) => {
      console.error("Error approving interlink:", error);
      setLastClickedId(null);
    },
  });

  const { mutate: editInterlinkSuggestion } = useMutation<
    void,
    Error,
    { id: number; final: string }
  >({
    mutationFn: async (data) => {
      await apiClient.sites.widgetRecommendationsPartialUpdate(
        Number(id),
        data.id,
        data,
      );
    },
    onSuccess: () => {
      if (onMutationSuccess) {
        onMutationSuccess();
      }
    },
    onError: (error) => {
      console.error("Error editing interlink:", error);
      setLastClickedId(null);
    },
  });

  const handleApprove = (detail: PageDetail, event: React.MouseEvent) => {
    event.stopPropagation();
    if (detail?.id) {
      setLastClickedId(detail.id);
      approve({
        kind: detail.active ? "unapprove" : "approve",
        ids: [detail.id],
        variant: "interlink",
      });
    }
  };

  const handleEditClick = (detail: PageDetail, event: React.MouseEvent) => {
    event.stopPropagation();
    setCurrentEditDetail(detail);

    setEditedSuggestion({
      final: detail.final || detail.suggestion || "",
      active: !!detail.active,
      lang: detail.lang,
    });

    setIsEditModalOpen(true);
  };

  const handleModalSubmit = () => {
    if (!currentEditDetail?.id) return;

    setLastClickedId(currentEditDetail.id);

    if (editedSuggestion.active !== currentEditDetail.active) {
      approve({
        kind: editedSuggestion.active ? "approve" : "unapprove",
        ids: [currentEditDetail.id],
        variant: "interlink",
      });
    }

    if (editedSuggestion.final !== currentEditDetail.final) {
      editInterlinkSuggestion({
        id: currentEditDetail.id,
        final: editedSuggestion.final,
      });
    }
  };

  const allApproved = Boolean(
    currentPage?.details?.every((item) => item.active),
  );

  const handleApproveAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    //@ts-expect-error be types
    if (!currentPage?.id || !currentPage.details?.[0]?.page_id) return;

    setIsApproveAllClicked(true);

    approve({
      kind: allApproved ? "unapprove" : "approve",
      //@ts-expect-error be types
      page_id: currentPage.details[0].page_id,
      variant: "interlink",
    });
  };

  return (
    <div className={styles.main}>
      <div className={styles.mainContent}>
        {isInitialLoading ? (
          <Spinner />
        ) : (
          <>
            <div className={styles.mainContentSidebar}>
              <div className={styles.pageListWrapper}>
                <div className={styles.pageList}>
                  <div className={styles.pageListHeader}>
                    <div>
                      <h2 className={styles.pageListTitle}>Interlinks</h2>
                      {totalCount > 0 && (
                        <div className={styles.pageListStats}>
                          {approvedCount === totalCount ? (
                            <span className={styles.pageListStatsAllApproved}>
                              {`All ${totalCount} Approved`}
                            </span>
                          ) : (
                            <>
                              <span
                                className={
                                  approvedCount > 0
                                    ? styles.pageListStatsApprovedCount
                                    : ""
                                }
                              >
                                {approvedCount}
                              </span>
                              {` of ${totalCount} approved`}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outlinedIconSmall"
                      onClick={() => setIsApproveAllModalOpen(true)}
                      disabled={isFetching || isApprovePending}
                    >
                      {isFetching || isApprovePending ? (
                        <Spinner size="1rem" />
                      ) : (
                        <Icon
                          iconName={
                            allApproved
                              ? "GreenCheckmarkIcon"
                              : "CloseIconCircled"
                          }
                          width="1rem"
                          height="1rem"
                        />
                      )}
                      {allApproved ? "All Approved" : "I’m Feeling Lucky"}
                    </Button>
                    <ApproveAllConfirmationModal
                      isOpen={isApproveAllModalOpen}
                      onClose={() => setIsApproveAllModalOpen(false)}
                      isAllApproved={allApproved}
                      toggleApprove={async () => {
                        await approveAsync({
                          kind: allApproved ? "unapprove" : "approve",
                          variant: "interlink",
                        });
                      }}
                      isApproveLoading={isApprovePending}
                      autoApprovalField="interlinks_auto_approval"
                    />
                  </div>
                  {description && (
                    <p className={styles.pageListDescription}>{description}</p>
                  )}
                  <div className={styles.pageListSearch}>
                    <SearchBar
                      onChange={setSearchTerm}
                      value={searchTerm}
                      placeholder="Search"
                    />
                  </div>
                  <>
                    {noData ? (
                      <NoData title="No Interlinks" />
                    ) : (
                      data?.map((page: PageItem) => {
                        const pageItem = {
                          id: page.id,
                          url: page.path_and_query,
                          title: page?.title,
                          approved: page.approved ?? 0,
                          total: page.total ?? 0,
                        };

                        return (
                          <div
                            className={`${styles.pageListItem} ${currentPage?.id === page.id ? styles.pageItemActive : ""}`}
                            onClick={() => setCurrentPage(page)}
                            key={page.id}
                          >
                            <PageListItem page={pageItem} />
                          </div>
                        );
                      })
                    )}
                  </>
                </div>
              </div>
              <div className={styles.mainContentFooter}>
                <Button
                  disabled={allPagesLoaded || isFetching}
                  variant="outlinedIconSmall"
                  onClick={() => {
                    if (
                      pagination.currentPage < (pagination?.totalPages ?? 0)
                    ) {
                      setIsLoadMoreClicked(true);
                      setPagination({
                        ...pagination,
                        currentPage: pagination.currentPage + 1,
                      });
                    }
                  }}
                >
                  {isFetching && isLoadMoreClicked ? (
                    <Spinner size="1rem" />
                  ) : null}
                  {allPagesLoaded ? "All Pages Loaded" : "Load More"}
                </Button>
              </div>
              {/*
               // @ts-expect-error TS2339 non-existing className */}
              <div ref={bottomRef} className={styles.bottomAnchor} />
            </div>
            <div className={styles.contentMain}>
              {currentPage && (
                // @ts-expect-error TS2551 non-existing className
                <div className={styles.pageItemWrapper}>
                  {noData ? (
                    <NoData title="No Interlinks Suggestions" />
                  ) : (
                    <>
                      <div className={styles.pageItemHeader}>
                        <div className={styles.pageItemUrl}>
                          <MiddleTruncateTextPx
                            text={currentPage?.path_and_query || ""}
                            maxWidth={300}
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          />
                        </div>
                        <Button
                          variant="outlinedIconSmall"
                          onClick={handleApproveAll}
                          disabled={isWidgetActionLoading}
                        >
                          {isFetching && isApproveAllClicked ? (
                            <Spinner size="1rem" />
                          ) : (
                            <Icon
                              iconName={
                                allApproved
                                  ? "GreenCheckmarkIcon"
                                  : "CloseIconCircled"
                              }
                              width="1rem"
                              height="1rem"
                            />
                          )}
                          {allApproved ? "Approved" : "Approve All"}
                        </Button>
                      </div>
                      <div className={styles.pageItemApproved}>
                        {currentPage.approved !== undefined &&
                          currentPage.total !== undefined &&
                          (allApproved ? (
                            <span>
                              {formatNumber(currentPage.approved)} approved
                            </span>
                          ) : (
                            <span>
                              {formatNumber(currentPage.approved)} of{" "}
                              {formatNumber(currentPage.total)} approved
                            </span>
                          ))}
                      </div>
                      <div className={styles.pageItemTitle}>
                        {currentPage?.details?.map((item, index) => (
                          <InterlinksItem
                            key={item.id || index}
                            item={item}
                            index={index}
                            isFirstItem={index === 0}
                            isWidgetActionLoading={isWidgetActionLoading}
                            lastClickedId={lastClickedId}
                            onApprove={handleApprove}
                            onEdit={handleEditClick}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Modal
        isLoading={isWidgetActionLoading}
        isOpen={isEditModalOpen}
        closeModal={() => setIsEditModalOpen(false)}
        buttonLabel="Update"
        formId="editSuggestion"
        modalLabel={
          // @ts-expect-error TS2339 non-existing className
          <h3 className={styles.modalTitle}>Edit Interlink Suggestion</h3>
        }
        action={handleModalSubmit}
      >
        <ContentSchemaModal
          editedSuggestion={editedSuggestion}
          setEditedSuggestion={setEditedSuggestion}
        />
      </Modal>
    </div>
  );
};

export default Interlinks;
